// File names for the exports a practice downloads: taxr_<client>_<period>.<ext>.
//
// The backend names the same file in its Content-Disposition header
// (_export_filename in backend/api/views/accountant_export.py) with the same
// rule. The portal is on another origin from the API and cannot read that
// header, so it builds the name itself. Keep the two in step: the vectors in
// tests/export-filename.spec.ts are the backend's.

// Longest the client's name may run in a file name.
const MAX_NAME_CHARS = 60;

const TRIM_EDGES = /^[._-]+|[._-]+$/g;

/**
 * `text` as something safe in a file name: accents dropped (Zoë becomes Zoe),
 * every run of anything but letters, digits, dot, underscore and hyphen
 * becomes one underscore, so a quote, slash or space typed into a client's name
 * never reaches the filesystem. Empty when nothing usable is left.
 */
export function filenamePart(text: string): string {
  const unaccented = (text ?? "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "");
  const cleaned = unaccented
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(TRIM_EDGES, "");
  return cleaned.slice(0, MAX_NAME_CHARS).replace(TRIM_EDGES, "");
}

/** The period of an export: both dates, one open end, or `all`. */
export function exportPeriodPart(dateFrom: string, dateTo: string): string {
  if (dateFrom && dateTo) return `${dateFrom}_to_${dateTo}`;
  if (dateFrom) return `from_${dateFrom}`;
  if (dateTo) return `to_${dateTo}`;
  return "all";
}

/**
 * `taxr_<client>_<period>.<ext>`. The client is the first of `clientNames`
 * with a usable character in it (the name the practice sees, then the email),
 * so two clients never share a name just because neither is written in Latin
 * letters.
 */
export function exportFilename(
  clientNames: string[],
  dateFrom: string,
  dateTo: string,
  ext: string,
): string {
  const name = clientNames.map(filenamePart).find(Boolean) ?? "client";
  return `taxr_${name}_${exportPeriodPart(dateFrom, dateTo)}.${ext}`;
}

/**
 * `name`, or `name` with _2, _3 ... before its extension when `used` already
 * has it, so two clients with the same name do not overwrite each other in a
 * zip. Adds the result to `used`.
 */
export function uniqueFilename(name: string, used: Set<string>): string {
  let candidate = name;
  const dot = name.lastIndexOf(".");
  const stem = dot === -1 ? name : name.slice(0, dot);
  const ext = dot === -1 ? "" : name.slice(dot);
  for (let n = 2; used.has(candidate); n++) candidate = `${stem}_${n}${ext}`;
  used.add(candidate);
  return candidate;
}
